// Regroupement VISUEL des rôles par famille métier : chaque direction voit son contributeur,
// son responsable et ses rôles satellites (Macroéconomie avec les Analyses économiques) sur le
// même segment, dans la page Permissions comme dans la fiche « Gérer » d'un utilisateur.
// Dérivé de la convention de nommage des rôles intégrés (contributeur-X / responsable-X) plus
// quelques rattachements explicites ; les rôles inconnus tombent dans « Autres rôles ».

export interface RoleLike {
	key: string;
	name?: string | null;
}

export interface RoleFamily<T extends RoleLike> {
	key: string;
	label: string;
	roles: T[];
}

/** Rattachements explicites : rôles dont la famille ne se lit pas dans le préfixe. */
const EXPLICIT_FAMILY: Record<string, string> = {
	macroeconomie: "analyses-economiques",
	// La lecture seule des messages de contact n'a pas de préfixe contributeur/responsable.
	"messages-contact": "messages-contact"
};

const FAMILY_LABELS: Record<string, string> = {
	communication: "Communication",
	"analyses-economiques": "Analyses économiques et macroéconomie",
	dobm: "Opérations bancaires et marchés",
	reglementation: "Réglementation",
	stabilite: "Stabilité financière",
	agrements: "Agréments",
	"messages-contact": "Messages de contact",
	transverse: "Rôles transverses",
	autres: "Autres rôles"
};

/** Ordre d'affichage des familles : les directions contributrices d'abord, le transverse ensuite. */
const FAMILY_ORDER = [
	"communication",
	"analyses-economiques",
	"dobm",
	"reglementation",
	"stabilite",
	"agrements",
	"messages-contact",
	"transverse",
	"autres"
];

/** Rôles transverses connus (ni contributeur-X ni responsable-X). */
const TRANSVERSE_KEYS = new Set([
	"administrator",
	"editor",
	"viewer",
	"translator",
	"deployer",
	"backup-manager",
	"responsable-publication"
]);

const familyOf = (key: string): string => {
	const k = (key ?? "").trim().toLowerCase();
	if (EXPLICIT_FAMILY[k]) return EXPLICIT_FAMILY[k];
	const m = /^(?:contributeur|responsable)-(.+)$/.exec(k);
	if (m) {
		const suffix = m[1];
		if (FAMILY_LABELS[suffix]) return suffix;
		// responsable-deploiement / responsable-publication : transverses, pas une direction.
		return TRANSVERSE_KEYS.has(k) ? "transverse" : "autres";
	}
	return TRANSVERSE_KEYS.has(k) ? "transverse" : "autres";
};

/** Rang d'un rôle dans sa famille : contributeur, puis responsable, puis les satellites. */
const rankInFamily = (key: string): number => {
	const k = key.toLowerCase();
	if (k.startsWith("contributeur-")) return 0;
	if (k.startsWith("responsable-")) return 1;
	return 2;
};

/** Regroupe les rôles par famille, familles et rôles ordonnés pour l'affichage. */
export const groupRolesByFamily = <T extends RoleLike>(roles: T[]): RoleFamily<T>[] => {
	const buckets = new Map<string, T[]>();
	for (const role of roles ?? []) {
		const family = familyOf(role.key);
		const list = buckets.get(family) ?? [];
		list.push(role);
		buckets.set(family, list);
	}
	const families: RoleFamily<T>[] = [];
	for (const familyKey of FAMILY_ORDER) {
		const list = buckets.get(familyKey);
		if (!list || list.length === 0) continue;
		list.sort((a, b) => rankInFamily(a.key) - rankInFamily(b.key) || (a.name ?? a.key).localeCompare(b.name ?? b.key, "fr"));
		families.push({ key: familyKey, label: FAMILY_LABELS[familyKey] ?? familyKey, roles: list });
	}
	return families;
};

export default groupRolesByFamily;
